import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDownIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { trackEvent } from '../lib/posthog';
import { useUserTypes } from '../hooks/useUserTypes';

interface AuthNavigationProps {
  isMobile?: boolean;
}

export default function AuthNavigation({ isMobile = false }: AuthNavigationProps) {
  const session = useSession();
  const supabase = useSupabaseClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [showCampsDropdown, setShowCampsDropdown] = useState(false);
  const [showFamilyDropdown, setShowFamilyDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const familyDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowCampsDropdown(false);
      }
      if (familyDropdownRef.current && !familyDropdownRef.current.contains(event.target as Node)) {
        setShowFamilyDropdown(false);
      }
    };

    if (showCampsDropdown || showFamilyDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCampsDropdown, showFamilyDropdown]);

  // Check admin status using RPC function
  const { data: isAdmin = false } = useQuery(
    ['is-admin', session?.user?.id],
    async () => {
      if (!session?.user?.id) return false;
      
      const { data, error } = await supabase
        .rpc('check_is_admin', {
          user_id: session.user.id
        });
      
      if (error) {
        console.error('Error checking admin status:', error);
        return false;
      }
      
      return !!data;
    },
    {
      enabled: !!session?.user?.id,
      staleTime: 30000, // Cache for 30 seconds
      cacheTime: 60000, // Keep in cache for 1 minute
      retry: false
    }
  );

  // Get user types from the user_types table
  const { isCampOwner, isParent } = useUserTypes();

  // Only check for owned camps if user is a camp owner
  const { data: ownedCamps = [] } = useQuery(
    ['owned-camps', session?.user?.id],
    async () => {
      if (!session?.user?.id || !isCampOwner) return [];
      
      const { data, error } = await supabase
        .from('camps')
        .select('id, name, slug, claimed, owner_id, plan_id, payment_status')
        .eq('owner_id', session.user.id);
      
      if (error) {
        console.error('Error checking owned camps:', error);
        return [];
      }
      
      return data || [];
    },
    {
      enabled: !!session?.user?.id && isCampOwner,
      staleTime: 30000, // Cache for 30 seconds
      cacheTime: 60000, // Keep in cache for 1 minute
      retry: false
    }
  );

  const handleSignOut = async () => {
    // Track sign out attempt first (while we still have session data)
    const userId = session?.user?.id;
    
    try {
      // Try to sign out from Supabase server
      const { error } = await supabase.auth.signOut();
      
      // If we get any error (403, session missing, etc.), manually clear local storage
      if (error) {
        console.log('Sign out error, manually clearing session:', error.message);
        
        // Manually clear all Supabase auth storage
        localStorage.removeItem('sb-weudqolihrchgtfpvexi-auth-token');
        sessionStorage.clear();
        
        // Also clear any other Supabase keys
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('sb-') && key.includes('auth')) {
            localStorage.removeItem(key);
          }
        });
        
        trackEvent('user_signed_out', {
          user_id: userId,
          timestamp: new Date().toISOString(),
          note: 'Manual storage clear due to API error'
        });
        
        // Force page reload to clear all state
        window.location.href = '/';
        return;
      }
      
      // Track successful sign out
      trackEvent('user_signed_out', {
        user_id: userId,
        timestamp: new Date().toISOString()
      });
      
      navigate('/');
      toast.success('Successfully signed out');
    } catch (err) {
      console.error('Error signing out:', err);
      
      // Fallback: manually clear storage and force reload
      localStorage.removeItem('sb-weudqolihrchgtfpvexi-auth-token');
      sessionStorage.clear();
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('sb-') && key.includes('auth')) {
          localStorage.removeItem(key);
        }
      });
      
      window.location.href = '/';
    }
  };

  const getLinkClasses = (path: string) => {
    const isActive = location.pathname === path;
    if (isMobile) {
      return `block px-4 py-3 text-base font-medium text-gray-900 hover:bg-gray-50 hover:text-primary transition-colors mb-3 ${
        isActive ? 'bg-gray-50 text-primary' : ''
      }`;
    }
    return `text-primary hover:text-primary/80 px-3 py-2 rounded-xl text-sm font-medium relative transition-all duration-300 ${
      isActive ? 'after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-secondary' : ''
    }`;
  };

  const getMobileButtonClasses = (variant: 'primary' | 'secondary' = 'primary') => {
    const baseClasses = 'w-full px-4 py-3 text-base font-medium rounded-lg transition-colors mb-3';
    if (variant === 'primary') {
      return `${baseClasses} bg-primary text-white hover:bg-primary/90`;
    }
    return `${baseClasses} bg-gray-100 text-gray-900 hover:bg-gray-200`;
  };

  const getMobileButtonStyle = (variant: 'primary' | 'secondary' = 'primary') => {
    if (variant === 'primary') {
      return {
        backgroundColor: 'rgb(82 0 196)',
        color: 'white'
      };
    }
    return {
      backgroundColor: '#f3f4f6',
      color: '#111827'
    };
  };

  if (session) {
        return (
          <>
               {/* Show My Camp(s) for camp owners and admins - go to their camp when they have one, else teaser */}
               {(isCampOwner === true || isAdmin) && (() => {
                 const claimedCamps = (ownedCamps || []).filter((c: { claimed?: boolean }) => c.claimed);
                 const myCampSlug = claimedCamps.length > 0 ? claimedCamps[0].slug : null;
                 const myCampPath = myCampSlug ? `/camps/${myCampSlug}` : '/mycamp-teaser';
                 const isActive = location.pathname === '/mycamp-teaser' || (myCampSlug && location.pathname === `/camps/${myCampSlug}`);
                 return (
                   <Link 
                     to={myCampPath}
                     className={isMobile ? getLinkClasses(myCampPath) : `btn btn-secondary ${isActive ? '!bg-primary !text-white' : ''}`}
                   >
                     My Camp
                   </Link>
                 );
               })()}
        
        {/* Family Center Dropdown - for parents and admins */}
        {(isParent || isAdmin) && (
          <div className="relative" ref={familyDropdownRef}>
            <button
              onClick={() => setShowFamilyDropdown(!showFamilyDropdown)}
              className={isMobile ? "block px-4 py-3 text-base font-medium text-gray-900 hover:bg-gray-50 hover:text-primary transition-colors w-full text-left" : "btn btn-secondary"}
              type="button"
            >
              Family Center
              {!isMobile && (
                <svg className="ml-1 h-4 w-4 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </button>
            {showFamilyDropdown && (
              <div className={isMobile ? "pl-8 space-y-1" : "absolute right-0 mt-2 w-48 rounded-lg shadow-lg bg-white border border-gray-200 z-50"}>
                <div className={isMobile ? "" : "py-1"}>
                  <Link
                    to="/kids-schedule"
                    onClick={() => setShowFamilyDropdown(false)}
                    className={isMobile 
                      ? "block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary transition-colors"
                      : "block px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors"
                    }
                  >
                    Kids Schedule
                  </Link>
                  <Link
                    to="/favorites"
                    onClick={() => setShowFamilyDropdown(false)}
                    className={isMobile 
                      ? "block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary transition-colors"
                      : "block px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors"
                    }
                  >
                    My Favorites
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}
        
        {isAdmin && (
          <Link 
            to="/admin" 
            className={isMobile ? getLinkClasses('/admin') : 'btn btn-secondary'}
          >
            Admin
          </Link>
        )}
        <button
          onClick={handleSignOut}
          className={isMobile ? getLinkClasses('/') : 'btn btn-secondary inline-flex items-center gap-1.5'}
          type="button"
        >
          <ArrowRightOnRectangleIcon className="h-4 w-4 sm:h-5 sm:w-5" />
          Sign Out
        </button>
      </>
    );
  }

  return (
    <>
      <Link 
        to="/login" 
        className={isMobile ? getLinkClasses('/login') : 'btn btn-sm btn-primary'}
      >
        Sign In / Register
      </Link>
    </>
  );
} 